import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Handle unhandled promise rejections for invalid refresh tokens
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason && 
          typeof event.reason === 'object' && 
          event.reason.message && 
          event.reason.message.includes('Invalid Refresh Token: Refresh Token Not Found')) {
        console.warn('Invalid refresh token detected, signing out user');
        signOut();
        event.preventDefault(); // Prevent the error from being logged to console
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // If successful, ensure the session is set immediately
    if (!error && data.session) {
      setSession(data.session);
      setUser(data.session.user);
    }

    return { error };
  };

  const signUp = async (email: string, password: string, profileData?: {
    firstName: string;
    lastName: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  }) => {
    // Validate inputs before sending to Supabase
    if (!email?.trim()) {
      return { error: { message: 'Email is required' } };
    }
    
    if (!password || password.length < 6) {
      return { error: { message: 'Password must be at least 6 characters long' } };
    }

    const { data, error } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: {
        emailRedirectTo: undefined, // Disable email confirmation
      },
    });

    // If signup is successful, create a complete user profile
    if (!error && data.user) {
      try {
        // Create a complete profile entry with all provided data
        const profileInsert = {
          first_name: profileData?.firstName?.trim() || '',
          last_name: profileData?.lastName?.trim() || '',
          phone: profileData?.phone?.trim() || '',
          address: profileData?.address?.trim() || '',
          city: profileData?.city?.trim() || '',
          state: profileData?.state?.trim() || '',
          zip_code: profileData?.zipCode?.trim() || '',
          country: profileData?.country?.trim() || 'United States'
        };

        const { error: profileError } = await supabase
          .from('user_profiles')
          .insert(profileInsert);

        if (profileError) {
          console.error('Error creating user profile:', profileError);
          // Don't fail signup if profile creation fails, but log it
        } else {
          console.log('User profile created successfully');
        }
      } catch (profileError) {
        console.error('Error creating initial profile:', profileError);
        // Don't fail signup if profile creation fails
      }
    }
    
    return { error };
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Sign out error:', error);
      }
      
      // Clear local state
      setUser(null);
      setSession(null);
      
      // Force redirect to home page
      window.location.href = '/';
      
      return { error };
    } catch (error) {
      console.error('Sign out error:', error);
      
      // Clear local state and redirect even if server call fails
      setUser(null);
      setSession(null);
      
      // Force redirect even on error
      window.location.href = '/';
      
      return { error };
    }
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};